import { useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';
import { firstOfType } from '@resolve-js/core';
import { useQueryBuilder } from '@resolve-js/react-hooks';
import { queryReadModelFailure, queryReadModelRequest, queryReadModelSuccess } from './actions';
import { isDependencies, isOptions } from '../helpers';
import { ResultStatus } from '../types';
import { badSelectorDrain, getEntry } from './read-model-reducer';
import { getSelectorState, releaseSelectorState, setSelectorState } from './initial-state-manager';
var defaultQueryOptions = {
  method: 'GET'
};
var defaultHookOptions = {};
var internalActions = {
  request: queryReadModelRequest,
  success: queryReadModelSuccess,
  failure: queryReadModelFailure
};

function isBuilder(query) {
  return typeof query === 'function';
}

function useReduxReadModel(query, initialState, options, dependencies) {
  var actualOptions = isOptions(options) ? options : defaultHookOptions;
  var actualActionCreators = actualOptions.actions || internalActions;

  if (isBuilder(query) && !actualOptions.selectorId && actualActionCreators === internalActions) {
    throw Error("Query builder function should be used with selector id or custom redux actions");
  }

  var actualDependencies = firstOfType(isDependencies, options, dependencies);
  var _request = actualActionCreators.request,
      success = actualActionCreators.success,
      failure = actualActionCreators.failure;
  var selectorId = actualOptions.selectorId;
  var actualSelector = selectorId || (!isBuilder(query) ? {
    query: query
  } : badSelectorDrain);

  var callback = function callback(error, result, executedQuery) {
    releaseSelectorState(actualSelector);

    if (error || result == null) {
      if (typeof failure === 'function') {
        if (error) {
          dispatch(failure(executedQuery, error, selectorId));
        } else {
          dispatch(failure(executedQuery, new Error("null response"), selectorId));
        }
      }
    } else {
      if (typeof success === 'function') {
        dispatch(success(executedQuery, result, selectorId));
      }
    }
  };

  var dispatch = useDispatch();
  var executor = actualDependencies ? useQueryBuilder(function (query) {
    return query;
  }, actualOptions.queryOptions || defaultQueryOptions, callback, actualDependencies) : useQueryBuilder(function (query) {
    return query;
  }, actualOptions.queryOptions || defaultQueryOptions, callback);
  var mutex = useRef({
    assigned: false,
    state: initialState,
    selector: actualSelector
  });

  if (!mutex.current.assigned) {
    setSelectorState(actualSelector, mutex.current.state);
    mutex.current.assigned = true;
  } else {
    if (!isEqual(mutex.current.selector, actualSelector) || !isEqual(mutex.current.state, initialState)) {
      releaseSelectorState(mutex.current.selector);
      setSelectorState(actualSelector, initialState);
      mutex.current.selector = actualSelector;
      mutex.current.state = initialState;
    }
  }

  return useMemo(function () {
    return {
      request: function request() {
        var dispatchRequest = function dispatchRequest(query) {
          if (typeof _request === 'function') {
            dispatch(_request(query, initialState, selectorId));
          }
        };

        var plainQuery = isBuilder(query) ? query.apply(void 0, arguments) : query;
        dispatchRequest(plainQuery);
        executor(plainQuery);
      },
      selector: function selector(state) {
        return getEntry(state.readModels, actualSelector, {
          status: ResultStatus.Initial,
          data: getSelectorState(actualSelector)
        });
      }
    };
  }, [executor, dispatch]);
}

export { useReduxReadModel };
//# sourceMappingURL=use-redux-read-model.js.map